/**
 * ログフィルターセクションコンポーネント
 *
 * 顧客選択、期間指定、クイック日付選択、異常のみ表示のフィルターを提供
 */

import { Stack, Group, Box, Switch, Flex, Button } from "@mantine/core";
import { DateTimePicker } from "@mantine/dates";
import { ArrowRight } from "lucide-react";
import { CustomerSelect } from "../common/CustomerSelect";
import dayjs from "dayjs";

interface LogsFilterSectionProps {
  customerIdFilter: string | null;
  onCustomerChange: (value: string | null) => void;
  dateRange: [Date | null, Date | null];
  onDateRangeChange: (range: [Date | null, Date | null]) => void;
  problematicOnly: boolean;
  onProblematicOnlyChange: (value: boolean) => void;
  onPageReset: () => void;
}

/**
 * ログフィルターセクション
 */
export function LogsFilterSection({
  customerIdFilter,
  onCustomerChange,
  dateRange,
  onDateRangeChange,
  problematicOnly,
  onProblematicOnlyChange,
  onPageReset,
}: LogsFilterSectionProps) {
  /**
   * クイック日付範囲を設定
   */
  const setQuickDateRange = (hours: number | "day" | "3days" | "week" | "month") => {
    const end = new Date();
    let start = new Date();

    if (typeof hours === "number") {
      start = dayjs().subtract(hours, "hour").toDate();
    } else if (hours === "day") {
      start = dayjs().startOf("day").toDate();
    } else if (hours === "3days") {
      start = dayjs().subtract(3, "day").toDate();
    } else if (hours === "week") {
      start = dayjs().subtract(1, "week").toDate();
    } else if (hours === "month") {
      start = dayjs().subtract(1, "month").toDate();
    }

    onDateRangeChange([start, end]);
    onPageReset();
  };

  return (
    <Stack gap="xs">
      {/* 上段: 対象の絞り込み（顧客 + 異常のみ） */}
      <Group justify="space-between" align="center">
        <Box style={{ flex: 1 }}>
          <CustomerSelect
            value={customerIdFilter}
            onChange={(val) => {
              onCustomerChange(val);
              onPageReset();
            }}
          />
        </Box>
        <Switch
          label="異常のみ表示"
          size="xs"
          checked={problematicOnly}
          onChange={(event) => {
            onProblematicOnlyChange(event.currentTarget.checked);
            onPageReset();
          }}
          style={{ cursor: "pointer" }}
          styles={{
            root: { cursor: "pointer" },
            input: { cursor: "pointer" },
            label: { cursor: "pointer" },
            track: { cursor: "pointer" },
          }}
        />
      </Group>

      {/* 下段: 時間軸の絞り込み（期間指定 + クイックボタン） */}
      <Stack gap="xs">
        <Flex gap="xs" align="center">
          <DateTimePicker
            placeholder="開始日時"
            value={dateRange[0]}
            onChange={(val: Date | null) => {
              onDateRangeChange([val, dateRange[1]]);
              onPageReset();
            }}
            size="xs"
            clearable
            locale="ja"
            valueFormat="MM/DD HH:mm"
            style={{ flex: 1, minWidth: 120 }}
          />
          <ArrowRight size={14} style={{ color: "var(--mantine-color-gray-5)", flexShrink: 0 }} />
          <DateTimePicker
            placeholder="終了日時"
            value={dateRange[1]}
            onChange={(val: Date | null) => {
              onDateRangeChange([dateRange[0], val]);
              onPageReset();
            }}
            size="xs"
            clearable
            locale="ja"
            valueFormat="MM/DD HH:mm"
            style={{ flex: 1, minWidth: 120 }}
          />
        </Flex>
        <Group gap={4} wrap="wrap">
          <Button variant="default" size="compact-xs" onClick={() => setQuickDateRange(1)}>
            1時間
          </Button>
          <Button variant="default" size="compact-xs" onClick={() => setQuickDateRange(6)}>
            6時間
          </Button>
          <Button variant="default" size="compact-xs" onClick={() => setQuickDateRange(24)}>
            24時間
          </Button>
          <Button variant="default" size="compact-xs" onClick={() => setQuickDateRange("3days")}>
            3日
          </Button>
          <Button variant="default" size="compact-xs" onClick={() => setQuickDateRange("week")}>
            1週間
          </Button>
          <Button variant="default" size="compact-xs" onClick={() => setQuickDateRange("month")}>
            1ヶ月
          </Button>
        </Group>
      </Stack>
    </Stack>
  );
}
